import { byId, el } from './dom';

/**
 * Sehr schlanker Dialog-Stack.
 *
 * Wichtig für die Android-App (TWA): Solange ein Dialog offen ist, liegt genau
 * ein zusätzlicher History-Eintrag vor. Die Zurück-Taste schließt damit erst
 * die Dialoge und beendet nicht sofort die App.
 *
 * Bewusst *ein* Eintrag für den gesamten Stapel: `history.back()` wirkt
 * asynchron. Ein Eintrag pro Dialog würde bei „schließen und sofort den
 * nächsten öffnen“ verspätet feuern und die App verlassen.
 */

interface OpenDialog {
  node: HTMLElement;
  dismissable: boolean;
  onClose?: () => void;
}

const stack: OpenDialog[] = [];

/** Liegt unser zusätzlicher History-Eintrag gerade vor? */
let guardActive = false;
/** Das nächste popstate stammt von uns selbst und darf nichts schließen. */
let ignoreNextPop = false;
/** Freigabe des History-Eintrags ist bereits eingeplant. */
let releaseScheduled = false;
let bound = false;

function root(): HTMLElement {
  return byId('dialog-root');
}

function bind(): void {
  if (bound) return;
  bound = true;

  window.addEventListener('popstate', () => {
    if (ignoreNextPop) {
      ignoreNextPop = false;
      return;
    }
    if (stack.length === 0) return;
    // Zurück-Taste: alle offenen Dialoge schließen, der Eintrag ist damit weg.
    guardActive = false;
    closeAllWithoutHistory();
  });

  root().addEventListener('click', (event) => {
    const top = stack.at(-1);
    if (event.target === root() && top?.dismissable) close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && stack.at(-1)?.dismissable) {
      event.preventDefault();
      close();
    }
  });
}

function render(): void {
  const container = root();
  container.replaceChildren();
  const top = stack.at(-1);
  if (!top) {
    container.hidden = true;
    return;
  }
  container.hidden = false;
  container.append(top.node);
}

/**
 * Gibt den History-Eintrag erst frei, wenn im selben Durchlauf kein neuer
 * Dialog geöffnet wurde (Menü → Untermenü behält den Eintrag).
 */
function scheduleGuardRelease(): void {
  if (!guardActive || releaseScheduled) return;
  releaseScheduled = true;
  setTimeout(() => {
    releaseScheduled = false;
    if (stack.length > 0 || !guardActive) return;
    guardActive = false;
    ignoreNextPop = true;
    history.back();
  }, 0);
}

function closeAllWithoutHistory(): void {
  while (stack.length > 0) {
    const entry = stack.pop()!;
    entry.onClose?.();
  }
  render();
}

export interface DialogOptions {
  title: string;
  /** Kurzer erklärender Text unter der Überschrift. */
  lead?: string;
  content: (Node | string)[];
  /** Klick auf den Hintergrund bzw. Escape schließt den Dialog (Standard: ja). */
  dismissable?: boolean;
  onClose?: () => void;
}

export function openDialog(options: DialogOptions): HTMLElement {
  bind();

  const dialog = el('div', { class: 'dialog', role: 'dialog', 'aria-modal': 'true', 'aria-label': options.title });
  dialog.append(el('h2', {}, [options.title]));
  if (options.lead) dialog.append(el('p', { class: 'lead' }, [options.lead]));
  for (const child of options.content) dialog.append(child);

  const entry: OpenDialog = { node: dialog, dismissable: options.dismissable !== false };
  if (options.onClose) entry.onClose = options.onClose;

  if (!guardActive) {
    history.pushState({ sudokuDialog: true }, '');
    guardActive = true;
  }

  stack.push(entry);
  render();
  return dialog;
}

/** Schließt den obersten Dialog. */
export function close(): void {
  const entry = stack.pop();
  if (!entry) return;
  render();
  entry.onClose?.();
  scheduleGuardRelease();
}

export function closeAll(): void {
  closeAllWithoutHistory();
  scheduleGuardRelease();
}

export function isDialogOpen(): boolean {
  return stack.length > 0;
}

/** Standardbutton für Dialoge. */
export function dialogButton(
  label: string,
  onClick: () => void,
  options: { meta?: string; primary?: boolean } = {},
): HTMLButtonElement {
  const button = el('button', { class: `button${options.primary ? ' primary' : ''}`, type: 'button' }, [label]);
  if (options.meta) button.append(el('span', { class: 'meta' }, [options.meta]));
  button.addEventListener('click', onClick);
  return button;
}
